<template>
  <AuthLayout>
    <template #header>
      <h1 class="text-[32px] font-semibold -mb-1.5">Acknowledgements</h1>
    </template>

    <div class="w-full">
      <div class="flex flex-col gap-3 mb-8 w-full">
        <label
          v-for="cb_input in acknowledgements"
          :key="cb_input.name"
          :for="cb_input.name"
          class="flex flex-col"
        >
          <div
            class="flex items-center text-strongest text-base px-2 py-3 rounded-xl leading-tight cursor-pointer"
            :class="
              authStore.signup_fields[cb_input.name]
                ? 'bg-radio-checkbox-selector font-medium'
                : 'bg-container-bg'
            "
          >
            <div
              class="w-4 h-4 min-w-4 min-h-4 rounded flex items-center justify-center"
              :class="
                authStore.signup_fields[cb_input.name]
                  ? 'bg-radio-checkbox-checked'
                  : 'border border-radio-checkbox-outline'
              "
            >
              <Icon
                v-if="authStore.signup_fields[cb_input.name]"
                name="Check"
                class="h-3 w-3 text-radio-checkbox-selector"
                :stroke-width="4"
              />
            </div>

            <input
              type="checkbox"
              :id="cb_input.name"
              v-model="authStore.signup_fields[cb_input.name]"
              class="sr-only"
            />

            <span class="ml-3 font-inter">
              {{ cb_input.label }}

              <!-- Welcome Packet -->
              <a
                v-if="cb_input.name === 'consent_cb'"
                href="#"
                class="underline font-inter"
                @click.stop.prevent="downloadWelcomePacketFiles"
              >
                {{ cb_input.action_text }}
              </a>

              <!-- Everything else -->
              <a
                v-else-if="cb_input.href"
                :href="cb_input.href"
                class="underline font-inter"
                target="_blank"
                @click.stop
              >
                {{ cb_input.action_text }}
              </a>
            </span>
          </div>

          <div
            v-if="attemptedSubmit && !authStore.signup_fields[cb_input.name]"
            class="mt-2 ml-3 font-inter text-error text-sm flex items-center"
          >
            <Icon name="Asterisk" class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error" />
            {{ cb_input.error_message }}
          </div>
        </label>
      </div>

      <Button
        @click="handleNextStep"
        size="md"
        variant="primary-grey"
        class="w-full max-lg:hidden"
        @keydown.enter="handleNextStep"
      >
        <template v-if="is_loading" #label>
          <IconPulseSpinner class="h-6 w-6" />
        </template>
        <template v-else #label> Next </template>
      </Button>
    </div>

    <template #cta>
      <Button @click="handleNextStep" size="md" variant="primary-grey" class="w-full">
        <template v-if="is_loading" #label>
          <IconPulseSpinner class="h-6 w-6" />
        </template>
        <template v-else #label> Next </template>
      </Button>
    </template>
  </AuthLayout>
</template>

<script setup>
import { onMounted, onUnmounted, computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";
import { Button, Icon } from "@/components";
import { IconPulseSpinner } from "@/components/icons";
import AuthLayout from "./AuthLayout.vue";

import Crescent_BCP_disclosure from "@/assets/documents/Crescent_BCP_disclosure.pdf";
import CSG_Form_CRS from "@/assets/documents/CSG_Form_CRS.pdf";
import CSG_Privacy_Policy from "@/assets/documents/CSG_Privacy_Policy.pdf";
import CSG_Reg_BI_Disclosure_Brochure_Phoenix_Energy from "@/assets/documents/CSG_Reg_BI_Disclosure_Brochure_Phoenix_Energy.pdf";

const router = useRouter();
const authStore = useAuthStore();
const { signupSubmitted, emailVerificationSent } = useAnalytics();
const is_loading = ref(false);
const attemptedSubmit = ref(false);

const acknowledgements = ref([
  {
    name: "consent_cb",
    label:
      "I have read and understand the information outlined by the company's broker-dealer in the",
    action_text: "Welcome Packet.",
    error_message: "Welcome Packet acknowledgement is required",
  },
  {
    name: "privacy_cb",
    label: "I consent to the",
    href: "https://phoenixenergy.com/privacy/",
    action_text: "Privacy Policy.",
    error_message: "Privacy Policy consent is required",
  },
  {
    name: "message_cb",
    label:
      "I consent to receive automated SMS messages and/or email marketing about my investment or relevant offers by or on behalf of Phoenix Energy and/or trusted affiliated companies at the phone number and email provided. I understand that consent is not a condition of purchase. Message and data rates may apply.",
    error_message: "Messaging consent is required",
  },
]);

function triggerDownload(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadWelcomePacketFiles() {
  const files = [
    {
      url: Crescent_BCP_disclosure,
      filename: "Crescent_BCP_disclosure.pdf",
    },
    {
      url: CSG_Form_CRS,
      filename: "CSG_Form_CRS.pdf",
    },
    {
      url: CSG_Privacy_Policy,
      filename: "CSG_Privacy_Policy.pdf",
    },
    {
      url: CSG_Reg_BI_Disclosure_Brochure_Phoenix_Energy,
      filename: "CSG_Reg_BI_Disclosure_Brochure_Phoenix_Energy.pdf",
    },
  ];

  files.forEach((file, index) => {
    setTimeout(() => {
      triggerDownload(file.url, file.filename);
    }, index * 150);
  });
}

onMounted(() => {
  window.scrollTo(0, 0);
  window.addEventListener("keydown", onKeydown);
  if (!authStore.signup_fields.email) {
    router.push("/sign-up");
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
});

const isFormValid = computed(() => {
  return (
    authStore.signup_fields.consent_cb &&
    authStore.signup_fields.message_cb &&
    authStore.signup_fields.privacy_cb
  );
});

function onKeydown(e) {
  if (e.key === "Enter") {
    handleNextStep();
  }
}

async function handleNextStep() {
  attemptedSubmit.value = true;

  if (!isFormValid.value) return;

  is_loading.value = true;
  authStore.mfa_channel = "Email";
  signupSubmitted({ method: "email" });
  await authStore.sendVerificationCode("Email");
  emailVerificationSent({ method: "email" });

  authStore.active_signup_step = 5;
  is_loading.value = false;
  router.push("/sign-up/auth-verification");
}

function handleBack() {
  authStore.active_signup_step = 3;
  router.push("/sign-up/set-password");
}
</script>
